/**
 * App-weites, abgeleitetes Reconnect-Banner (paymentlinks-7days).
 *
 * Zeigt auf JEDER Admin-Seite einen Warn-Hinweis, sobald
 * `mollieConnectionStatus.needsReconnect === true` ist — also wenn dem OAuth-Grant
 * ein Scope aus `OAUTH_SCOPES` fehlt (#1399; vorher nur `payment-links.write`).
 *
 * BEWUSSTE Designentscheidung: KEIN persistenter Announcement-Datensatz
 * (Lifecycle-/Dedupe-/Stale-Probleme), sondern ein ABGELEITETES Banner, das
 * live aus dem Connection-Status rendert. Nach erfolgtem Reconnect liefert das
 * Resolver-Feld false → Banner verschwindet automatisch, ohne State-Pflege.
 *
 * Gating (kritisch):
 *   (a) featureMollie aktiv (sonst ist Mollie gar nicht relevant)
 *   (b) eingeloggter User ist Admin/AreaManager/SuperAdmin/Superuser — denn der
 *       Resolver `mollieConnectionStatus` ist `@Authorized([Admin, Superuser])`
 *       und die Rollen-Hierarchie (customAuthChecker.roleImplies) lässt
 *       Superuser > SuperAdmin > AreaManager > Admin passieren. Für niedrigere
 *       Rollen (Instructor/Staff) würde der Query FORBIDDEN werfen → daher skip.
 *
 * React 17 / antd v4 konform: funktionale Komponente, antd v4 `<Alert banner>`.
 */
import React, {FC} from "react";
import {Alert} from "antd";
import {
    useGetCompanyInfoQuery,
    useMollieConnectionStatusQuery,
} from "../../generated/graphql";
import useHighestRole from "../../helpers/useHighestRole";
import Roles from "../../../shared/models/roles";
import {URL_SETTINGS_GENERAL} from "../../models/url";
import i18n from "../../services/i18n";

// Rollen, die laut customAuthChecker den Resolver @Authorized([Admin, Superuser])
// passieren (Hierarchie: Superuser > SuperAdmin > AreaManager > Admin).
const ROLES_WITH_MOLLIE_STATUS_ACCESS: Array<Roles> = [
    Roles.Superuser,
    Roles.SuperAdmin,
    Roles.AreaManager,
    Roles.Admin,
];

const MollieReconnectBanner: FC = () => {
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureMollie =
        !!companyInfoData?.companyInfo?.subscription?.featureMollie;

    const highestRole = useHighestRole();
    const hasStatusAccess = ROLES_WITH_MOLLIE_STATUS_ACCESS.includes(highestRole);

    // Query nur ausführen, wenn Feature aktiv UND der User den Resolver passieren
    // darf — sonst FORBIDDEN (Log-Flut + nutzloser Roundtrip).
    const {data} = useMollieConnectionStatusQuery({
        skip: !featureMollie || !hasStatusAccess,
        fetchPolicy: "cache-and-network",
    });

    const status = data?.mollieConnectionStatus;
    const onboardingIncomplete = status?.onboardingStatus === "needs-data";
    const needsReconnect = status?.needsReconnect === true;

    // Priorität: Onboarding unvollständig (blockiert Zahlungsempfang komplett) vor
    // dem Reconnect-Hinweis (Bestand ist bereits operativ).
    if (onboardingIncomplete) {
        const href = status?.dashboardUrl || `${URL_SETTINGS_GENERAL}/mollie`;
        const external = !!status?.dashboardUrl;
        return (
            <Alert
                type="warning"
                banner
                showIcon
                message={i18n.containers.common.MollieReconnect.onboardingBannerTitle()}
                description={
                    <div>
                        <span style={{marginRight: 8}}>
                            {i18n.containers.common.MollieReconnect.onboardingBannerDescription()}
                        </span>
                        <a
                            href={href}
                            {...(external ? {target: "_blank", rel: "noopener noreferrer"} : {})}
                            data-testid="mollie-onboarding-banner-link"
                        >
                            {i18n.containers.common.MollieReconnect.onboardingBannerLink()}
                        </a>
                    </div>
                }
                data-testid="mollie-onboarding-banner"
            />
        );
    }

    if (!needsReconnect) {
        return null;
    }

    return (
        <Alert
            type="warning"
            banner
            showIcon
            message={i18n.containers.common.MollieReconnect.bannerTitle()}
            description={
                <div>
                    <span style={{marginRight: 8}}>
                        {i18n.containers.common.MollieReconnect.bannerDescription()}
                    </span>
                    {/*
                     * Bewusst plain <a> statt react-router <Link>: Dieses Banner
                     * wird in App.tsx ALS GESCHWISTER von <AppRoutes/> gerendert,
                     * also AUSSERHALB des <Router>. Ein <Link> wuerde hier
                     * "Invariant failed: You should not use <Link> outside a
                     * <Router>" werfen und die gesamte App via ErrorHandler
                     * weiss-schalten. Voller Reload ist fuer die seltene
                     * Reconnect-Aktion akzeptabel.
                     */}
                    <a href={`${URL_SETTINGS_GENERAL}/mollie`} data-testid="mollie-reconnect-banner-link">
                        {i18n.containers.common.MollieReconnect.bannerLink()}
                    </a>
                </div>
            }
            data-testid="mollie-reconnect-banner"
        />
    );
};

export default MollieReconnectBanner;
